import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AssignBadgeInput,
  ListParticipantBadgesResponse,
  ParticipantBadge,
  RevokeBadgeInput,
} from 'shared';

export type { ParticipantBadge };

// ─── Query key factory ────────────────────────────────────────────────────────

export const participantBadgeKeys = {
  all: ['participantBadges'] as const,
  forParticipant: (id: number) => [...participantBadgeKeys.all, id] as const,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** All badge assignments (active + revoked) for a single participant. */
export function useParticipantBadges(participantId: number | null) {
  return useQuery({
    queryKey: participantBadgeKeys.forParticipant(participantId!),
    queryFn: () =>
      api.get<ListParticipantBadgesResponse>(`/api/participants/${participantId}/badges`),
    enabled: participantId !== null && participantId > 0,
    staleTime: 1000 * 30,
  });
}

/** Assign a badge to a participant. */
export function useAssignBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      participantId,
      data,
    }: {
      participantId: number;
      data: AssignBadgeInput;
    }) =>
      api.post<ParticipantBadge>(`/api/participants/${participantId}/badges`, data),
    onSuccess: (_, { participantId }) => {
      qc.invalidateQueries({
        queryKey: participantBadgeKeys.forParticipant(participantId),
      });
    },
  });
}

/** Revoke an active badge assignment from a participant. */
export function useRevokeBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      participantId,
      badgeId,
      data,
    }: {
      participantId: number;
      badgeId: string;
      data?: RevokeBadgeInput;
    }) =>
      api.delete<void>(
        `/api/participants/${participantId}/badges/${badgeId}`,
        data,
      ),
    onSuccess: (_, { participantId }) => {
      qc.invalidateQueries({
        queryKey: participantBadgeKeys.forParticipant(participantId),
      });
    },
  });
}
